import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { onError } from '@apollo/client/link/error';
import { Observable } from '@apollo/client/utilities';
import * as Sentry from '@sentry/browser';

const IMPERSONATION_NOT_ALLOWED_MESSAGE = 'Impersonation is not allowed';

function isUnauthorizedError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as Record<string, unknown>).name === 'ServerError' &&
    (error as Record<string, unknown>).statusCode === 401
  );
}

function isImpersonationError(error: unknown): boolean {
  return CombinedGraphQLErrors.is(error) && error.errors.some(e => e.message === IMPERSONATION_NOT_ALLOWED_MESSAGE);
}

export const createErrorLink = (onUnauthenticatedError: () => Promise<void>) =>
  onError(({ error, forward, operation }) => {
    if (isUnauthorizedError(error)) {
      // If the network error is a 401, we want to handle it by triggering the unauthenticated error handler.
      // This is an "expected" error in the sense that it indicates the user is not authenticated,
      // so we handle it differently than other errors, not capturing it with Sentry
      return new Observable(() => {
        void onUnauthenticatedError();
      });
    }

    if (isImpersonationError(error)) {
      // Impersonation errors are expected when an agent attempts a mutation while in impersonation mode.
      // The backend blocks these via BlockImpersonation, so we don't report them to Sentry.
      return forward(operation);
    }

    Sentry.captureException(error);

    return forward(operation);
  });
